import { useContext, useEffect, useRef } from "react";
import { useMap, useMapEvents } from "react-leaflet";

import { basemapLayer, Basemaps } from "esri-leaflet";
import { LatLng, Point } from "leaflet";
import { autorun } from "mobx";
import { observer } from "mobx-react-lite";
import { onPatch } from "mobx-state-tree";
import config from "./config";
import {
  cameraOverlapId,
  MarkerOverlapProvider,
  sensorOverlapId,
} from "./contexts/MarkerOverlapContext";
import RadarContext from "./contexts/RadarContext";
import WrfContext from "./contexts/WrfContext";
import CameraLayer from "./layers/CameraLayer";
import CbrfcLayer from "./layers/CbrfcLayer";
import LightningLayer from "./layers/LightningLayer";
import NwsWarningsLayer from "./layers/NwsWarningsLayer";
import PrecipSensorsLayer from "./layers/PrecipSensorsLayer";
import RadarLayer from "./layers/RadarLayer";
import RadarLayerLegacy from "./layers/RadarLayerLegacy";
import WrfLayer from "./layers/WrfLayer";
import WrfWindArrows from "./layers/WrfWindArrows";
import StaticLayer from "./layers/StaticLayer";
import StreamSensorsLayer from "./layers/StreamSensorsLayer";
import { BaseMap } from "./models/Layers";
import { useRootStore } from "./models/Root";
import { SensorDisplayPrecip, SensorDisplayStream } from "./models/SensorDisplay";

const baseMapsToShow = (layer: BaseMap): Basemaps[] => {
  switch (layer) {
    case "ImageryLabels":
      return [layer, "Imagery"];
    case "TerrainLabels":
      return [layer, "Terrain"];
    default:
      return [layer];
  }
};

const Map = () => {
  const radarContext = useContext(RadarContext);
  const radarReady = radarContext?.timeInfo || radarContext?.legacyInfo;
  const radarVisible = radarContext?.visible;
  const opacity = radarContext?.opacity || 1;
  const radarLegacy = config.radarLegacy === true;

  const wrfContext = useContext(WrfContext);

  const {
    layers: layerStore,
    sensors: {
      loading,
      display,
      precipSensors,
      selected: selectedSensor,
      streamSensors,
    },
    map: { setZoom },
    menus,
    isMobile,
  } = useRootStore();

  // Radar and Forecast are mutually exclusive full-map overlays. Opening one of
  // those panels hides the other so they don't render simultaneously. (Closing
  // a panel leaves its overlay up — only switching to the sibling resets it.)
  // If a third mutually-exclusive overlay is ever added (e.g. QPE), extract this
  // into a useOverlayMutex hook instead of chaining else-ifs here.
  const radarSetVisible = radarContext?.setVisible;
  const wrfSetVisible = wrfContext?.setVisible;
  useEffect(() => {
    if (menus.panel === "radar") wrfSetVisible?.(false);
    else if (menus.panel === "forecast") radarSetVisible?.(false);
  }, [menus.panel, radarSetVisible, wrfSetVisible]);

  const mapEvents = useMapEvents({
    zoomend: () => setZoom(mapEvents.getZoom()),
  });

  const map = useMap();
  const { style, classList } = map.getContainer();
  style.cursor = "crosshair";
  classList[loading ? "add" : "remove"]("sensors-loading");
  const baseMap = layerStore.baseMap;
  const baseLayers = useRef<ReturnType<typeof basemapLayer>[]>([]);

  useEffect(() => {
    baseLayers.current.forEach((layer) => map.removeLayer(layer));
    baseLayers.current = baseMapsToShow(baseMap).map((name) =>
      basemapLayer(name).addTo(map)
    );

    return () => {
      baseLayers.current.forEach((layer) => map.removeLayer(layer));
      baseLayers.current = [];
    };
  }, [baseMap, map]);

  autorun(() => {
    if (map && selectedSensor !== undefined) {
      const {
        sensor: { latitude, longitude },
      } = selectedSensor;
      const sensorLocation = { lat: latitude, lng: longitude };

      if (isMobile) {
        const { y: height } = map.getSize();
        map.panInside(sensorLocation, {
          animate: true,
          paddingTopLeft: new Point(24, 24),
          paddingBottomRight: new Point(24, Math.max(280, height * 0.55)),
        });
        return;
      }

      const bounds = map.getBounds().pad(-0.2);
      const inBounds = bounds.contains(sensorLocation);
      if (!inBounds) {
        map.flyTo(sensorLocation);
      }
    }
  });

  onPatch(layerStore, ({ path, value }) => {
    if (path === "/lightning" && value !== undefined) {
      // We have just turned on lightning layer
      if (isMobile) {
        menus.closePanel();
      }
    }
  });

  const activeSensors = SensorDisplayPrecip.is(display)
    ? precipSensors
    : SensorDisplayStream.is(display)
    ? streamSensors
    : [];
  const visibleCameras = layerStore.flattenMisc
    .filter(({ visible }) => visible)
    .flatMap(({ cameras }) => cameras);
  const overlapMarkers = [
    ...activeSensors.map((sensor) => ({
      id: sensorOverlapId(sensor.type, sensor.id),
      position: sensor.position,
    })),
    ...visibleCameras.map((camera) => ({
      id: cameraOverlapId(camera.url),
      position: new LatLng(camera.location.lat, camera.location.lng),
    })),
  ];

  return (
    <MarkerOverlapProvider markers={overlapMarkers}>
      {SensorDisplayPrecip.is(display) && (
        <PrecipSensorsLayer display={display}></PrecipSensorsLayer>
      )}
      {SensorDisplayStream.is(display) && (
        <StreamSensorsLayer display={display}></StreamSensorsLayer>
      )}
      {radarReady && radarVisible && (
        radarLegacy
          ? <RadarLayerLegacy opacity={opacity} />
          : <RadarLayer opacity={opacity} />
      )}
      {wrfContext?.visible && wrfContext?.activeApiResponse && (
        <WrfLayer opacity={wrfContext.opacity} />
      )}
      {wrfContext?.visible &&
        wrfContext?.product === "wind" &&
        wrfContext?.activeApiResponse && <WrfWindArrows />}
      {layerStore.flattenStatic.map((model, idx) => (
        <StaticLayer model={model} key={idx}></StaticLayer>
      ))}
      {layerStore.flattenMisc.map((model, idx) => (
        <CameraLayer key={idx} model={model}></CameraLayer>
      ))}
      {layerStore.lightning && (
        <LightningLayer lightning={layerStore.lightning} />
      )}
      {layerStore.cbrfc && <CbrfcLayer cbrfc={layerStore.cbrfc} />}
      {config.layers.nwsWarnings && layerStore.nwsWarnings && (
        <NwsWarningsLayer />
      )}
    </MarkerOverlapProvider>
  );
};

export default observer(Map);
